// Owns a single BerryBot conversation: id, transcript, current cart / hint /
// fulfillment, and the in-flight flags. Implemented as a Lit `ReactiveController`
// so `<bb-widget>` can host one instance and have every panel — chat(s),
// cart, log — read from the same source of truth and re-render on change.
//
// The controller does not touch the DOM and dispatches no events; the host
// element is responsible for re-emitting `conversation-started` / `state-change`
// to the page (it gets `onStart` / `onResponse` callbacks for that).

import type { ReactiveController, ReactiveControllerHost } from 'lit'
import {
  ApiError,
  apiErrorMessage,
  correlationHeaders,
  type BerryBotApi,
} from './api'
import type {
  Cart,
  CartAddRequest,
  CartItem,
  CartRemoveRequest,
  AutocompleteSuggestion,
  ContactRequest,
  FulfillmentRequest,
  PlaceDetailsResponse,
  Hint,
  LLMResponse,
  ParsedAddress,
} from './types'

export interface ChatMessage {
  who: 'user' | 'bot'
  text: string
  // The hint that arrived with this bot message, if any — kept on the message
  // so the chat panel can render its affordance inline and leave it in place
  // (disabled) once a newer turn supersedes it.
  hint?: Hint | null
}

export interface ConversationControllerOptions {
  // Storefront pinned for the conversation (passed to `startConversation`).
  getStorefrontId?: () => string | undefined
  // Invoked once per successful `start()`, with the first response.
  onStart?: (conversationId: string, response: LLMResponse) => void
  // Invoked for every response (including the first), after state is applied.
  onResponse?: (response: LLMResponse) => void
}

export class ConversationController implements ReactiveController {
  conversationId: string | null = null
  messages: ChatMessage[] = []
  cart: Cart | null = null
  fulfillmentType: string | null = null
  hint: Hint | null = null
  error: string | null = null
  starting = false
  pending = false
  // True while a `POST /orders/submit` is in flight (cart-side direct
  // submit). Separate from `pending` (turn in flight) so the widget can
  // show a distinct "Submitting your order…" overlay without conflating
  // it with normal chat-turn busy-ness.
  submitting = false
  // In-flight `POST /contact` promise, if any. submitOrder() awaits it
  // so the backend's re-review on submit reads the freshly-patched
  // metadata. Cleared when the POST resolves.
  contactPending: Promise<void> | null = null
  // Mirrors BerryBot's `response.conversation_state`. Reaches `"complete"`
  // either via an LLM turn that submits the order (chat-side path) or by
  // the controller's own `submitOrder` (cart-side path) — both routes
  // converge here so the thank-you screen fires for either.
  conversationState: string | null = null
  // Set once the order has been submitted. BerryBot surfaces it on every
  // response after submission via `response.order_id`; the cart-side
  // direct-submit path patches it from the relay's submit response.
  orderId: string | null = null
  // ISO-8601 (store-local) of the picked future fulfillment time, when
  // the customer scheduled the order via the scheduled_time picker.
  // Null for a now-order (ASAP). Surfaced on the /orders/submit response;
  // drives the thank-you screen's copy so a scheduled order doesn't
  // claim the kitchen has already started.
  scheduledFor: string | null = null
  // Monotonic counter bumped once per applied bot utterance (response with
  // non-null `message`). The widget watches this to detect "a fresh bot
  // turn just landed" so it can hold an overlay-eligible hint for one
  // turn — giving the user a chance to read the bot reply before the
  // modal steals focus (Trello #192).
  botTurnSeq = 0
  // In-memory log of widget-side affordances the customer used between
  // their last typed message and the next one — pre-formatted English
  // strings (e.g. `customer closed the modifier picker for "Salmon Poke
  // Bowl" without adding it to the cart`). Pushed by bb-widget's
  // affordance sink, flushed in `send()` as `client_context.ui_events`
  // and cleared. Without this the LLM is blind to silent taps and
  // mis-resolves anaphora like "never mind" / "cancel that" (Trello
  // #166). Strings are authored where the click happens so the
  // backend just joins + brackets them.
  private _uiEvents: string[] = []

  private host: ReactiveControllerHost
  private getApi: () => BerryBotApi | null
  private opts: ConversationControllerOptions

  constructor(
    host: ReactiveControllerHost,
    getApi: () => BerryBotApi | null,
    opts: ConversationControllerOptions = {},
  ) {
    this.host = host
    this.getApi = getApi
    this.opts = opts
    host.addController(this)
  }

  hostConnected() {
    void this.start()
  }

  hostDisconnected() {}

  // Any in-flight network action — turn, cart mutation, or order submit.
  get busy(): boolean {
    return this.starting || this.pending || this.submitting
  }

  async start(): Promise<void> {
    const api = this.getApi()
    if (!api || this.starting) return
    // Reset every field that survives a re-start so the host's "Start a
    // new order" button gives a truly clean conversation (no leftover
    // cart, hint, transcript, completion state, etc.).
    this.conversationId = null
    this.messages = []
    this.cart = null
    this.fulfillmentType = null
    this.hint = null
    this.conversationState = null
    this.orderId = null
    this.scheduledFor = null
    this.botTurnSeq = 0
    this.starting = true
    this.error = null
    this.host.requestUpdate()
    try {
      // Test/real tagging: a `?source=` URL param (e.g. pw sets
      // ?source=pw) rides into the conversation's metadata as
      // test_source. Absent for real customers → backend defaults to
      // "customer". Mirrors the existing ?env= pattern.
      const source =
        typeof location !== 'undefined'
          ? new URLSearchParams(location.search).get('source')
          : null
      const resp = await api.startConversation({
        client_type: 'chat',
        storefront_id: this.opts.getStorefrontId?.() || undefined,
        ...(source ? { metadata: { test_source: source } } : {}),
      })
      this.conversationId = resp.conversation_id
      this.applyResponse(resp.response)
      this.opts.onStart?.(resp.conversation_id, resp.response)
    } catch (e) {
      this.error = `Couldn't start the conversation: ${apiErrorMessage(e)}`
    } finally {
      this.starting = false
      this.host.requestUpdate()
    }
  }

  // Append a one-line English description of a widget affordance the
  // customer just used (picker cancel, hint dismiss, etc.) — see
  // Trello #166. Flushed and cleared on the next `send()`. No-op for
  // empty / non-string input. Capped at 32 entries: in normal use a
  // customer accumulates only a handful between turns; a runaway loop
  // (rapid clicks / focus thrash) shouldn't blow the prompt budget.
  pushUiEvent(text: string): void {
    if (typeof text !== 'string') return
    const trimmed = text.trim()
    if (!trimmed) return
    if (this._uiEvents.length >= 32) this._uiEvents.shift()
    this._uiEvents.push(trimmed)
  }

  async send(text: string): Promise<void> {
    const api = this.getApi()
    const trimmed = text.trim()
    if (!api || !this.conversationId || !trimmed || this.pending) return
    this.messages = [...this.messages, { who: 'user', text: trimmed }]
    this.pending = true
    this.error = null
    // Drain the ui-event buffer onto this turn. Cleared before the await
    // so a concurrent push during the in-flight request lands in the
    // next turn's buffer, not this one. Empty list → no client_context
    // (server treats absent the same as empty, but we keep the wire
    // payload minimal).
    const uiEvents = this._uiEvents
    this._uiEvents = []
    this.host.requestUpdate()
    const startedAt = nowMs()
    try {
      const resp = await api.sendUtterance(this.conversationId, {
        text: trimmed,
        ...(uiEvents.length
          ? { client_context: { ui_events: uiEvents } }
          : {}),
      })
      this.applyResponse(resp.response)
    } catch (e) {
      this.error = `Send failed: ${apiErrorMessage(e)}`
      // Berrybot's `turns` table only records requests its handler ran, so
      // a failed utterance that never reached the server (network abort,
      // 5xx at the edge) leaves no trace in the conversation log — hiding
      // an error the customer actually saw. Log a rich client event next
      // to what would have been the utterance turn: every field here has
      // a specific diagnostic purpose (see the shape and comments below).
      void api.postClientEvent(
        this.conversationId,
        'send.failed',
        buildSendFailurePayload(trimmed, e, {
          durationMs: Math.round(nowMs() - startedAt),
          uiEventsSent: uiEvents,
          hintKind: this.hint
            ? `${this.hint.type}:${this.hint.subject}`
            : null,
          transcriptLength: this.messages.length,
          cartItemCount: this.cart?.items?.length ?? 0,
          fulfillmentType: this.fulfillmentType,
          lastBotMessage: findLastBotMessage(this.messages),
        }),
      )
    } finally {
      this.pending = false
      this.host.requestUpdate()
    }
  }

  // Set fulfillment via the direct endpoint (not an LLM turn) — it geocodes +
  // zone-checks a delivery `address` (or trusts `parsed_address.coordinates` when
  // present, e.g. a sample address) and 422s with a detail if it can't. Then we
  // *narrate it into the conversation* with a real follow-up utterance: that keeps
  // the agent aware (rather than mutating state behind its back) and lets
  // `build_hint` surface the `submit_order` confirmation hint on the agent's reply
  // (it keys off the last user message). The narration does NOT restate the choice
  // or the address — the agent reads `fulfillment_type` / `address_confirmed` from
  // state; restating an address would make the agent re-geocode it (and a sample
  // address can be a Plus Code the geocoder rejects). We don't refetch `/state`
  // either — fulfillment doesn't change the cart, and `/state`'s cart is a JSON
  // string in a different shape; this narration turn refreshes it normally.
  async setFulfillment(req: FulfillmentRequest): Promise<void> {
    const api = this.getApi()
    if (!api || !this.conversationId || this.pending) return
    this.pending = true
    this.error = null
    this.host.requestUpdate()
    let resp
    try {
      resp = await api.setFulfillment(this.conversationId, req)
    } catch (e) {
      this.error = apiErrorMessage(e)
      this.pending = false
      this.host.requestUpdate()
      return
    }
    // Pre-`f7c8aa0` (May 2026) this method chained a synthetic
    // "Okay, I'm ready to check out." utterance after the silent
    // `/fulfillment` mutation — a full LLM round-trip whose only purpose
    // was to give `build_hint` a user message to anchor the
    // `submit_order` confirmation hint to. The backend now emits that
    // hint directly in the `/fulfillment` response, and injects a
    // synthetic assistant message into the conversation history so the
    // agent still sees the state change on its next real turn. Same
    // outcome, ~5–7s faster per fulfillment change.
    this.fulfillmentType = req.fulfillment_type
    if (resp?.hint !== undefined) this.hint = resp.hint
    // The backend injects an AIMessage ack into workflow state on every
    // fulfillment change (so the agent sees the choice on its next turn).
    // Mirror it into the transcript so the new submit_order hint anchors
    // to a real bot message — otherwise the previous fulfillment-chip
    // hint card stays "last" and the UI doesn't progress to "Place my
    // order". The synthetic message uses the same text the agent saw.
    if (resp?.assistant_message) {
      this.messages = [
        ...this.messages,
        {
          who: 'bot',
          text: resp.assistant_message,
          hint: resp.hint ?? null,
        },
      ]
    }
    this.pending = false
    this.host.requestUpdate()
  }

  // Google Places autocomplete session token. Generated lazily when the
  // delivery sub-form opens and reset after a place is picked OR after
  // the form closes — Google bills "one autocomplete sequence + the
  // place details fetch that resolves it" as ONE session at the SDK
  // tier, so token continuity is what keeps the bill at a single
  // session per address pick.
  private _autocompleteSessionToken: string | null = null

  private _newAutocompleteToken(): string {
    return (
      (crypto.randomUUID?.() as string | undefined)
      ?? `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
    )
  }

  startAutocompleteSession(): string {
    if (!this._autocompleteSessionToken) {
      this._autocompleteSessionToken = this._newAutocompleteToken()
    }
    return this._autocompleteSessionToken
  }

  endAutocompleteSession(): void {
    this._autocompleteSessionToken = null
  }

  async addressAutocomplete(
    query: string,
  ): Promise<AutocompleteSuggestion[]> {
    const api = this.getApi()
    if (!api || !this.conversationId || !query.trim()) return []
    try {
      const r = await api.addressAutocomplete(
        this.conversationId,
        query,
        this.startAutocompleteSession(),
      )
      return r.suggestions || []
    } catch (e) {
      console.warn('[bb-widget] addressAutocomplete failed', e)
      return []
    }
  }

  async addressDetails(placeId: string): Promise<PlaceDetailsResponse | null> {
    const api = this.getApi()
    if (!api || !this.conversationId || !placeId) return null
    try {
      const r = await api.addressDetails(
        this.conversationId,
        placeId,
        this.startAutocompleteSession(),
      )
      // Picking a place closes the Google session — next typeahead
      // burst starts a fresh one.
      this.endAutocompleteSession()
      return r
    } catch (e) {
      console.warn('[bb-widget] addressDetails failed', e)
      return null
    }
  }

  // Patch the conversation's contact metadata (phone / email / first /
  // last name). Fire-and-forget on the controller level — failures
  // surface as a logged warning rather than blocking the UI, because
  // the CTA gate is already driven by host-side validation and the
  // user can always retry submit. The host page calls this on its
  // `contact-submit` event after passing its own validation.
  async setContact(req: ContactRequest): Promise<void> {
    const api = this.getApi()
    if (!api || !this.conversationId) return
    // Track the in-flight POST so submitOrder() can await it.
    // Without this gate, a user who clicks "Place my order" within
    // ~50ms of the form passing validation submits before /contact
    // lands — the backend re-builds the order, but its metadata is
    // still empty so it falls back to the sample-address defaults
    // (+1 / 2025550123 / no email). The race is small but real and
    // deterministic in tests where the form-fill and CTA-click run
    // back-to-back.
    const p = (async () => {
      try {
        await api.setContact(this.conversationId!, req)
      } catch (e) {
        // Don't bubble — surfacing this in the chat error slot would
        // confuse the user (their input was fine; the network blipped).
        // The next submit attempt will re-trigger setContact via the
        // host page's flow.
        console.warn('[bb-widget] setContact failed', e)
      }
    })()
    this.contactPending = p
    try {
      await p
    } finally {
      if (this.contactPending === p) this.contactPending = null
    }
  }

  // Cart mutations via the direct relay endpoints. The relay only supports
  // *incrementing* `cart_add(item_name, +quantity)` and *whole-line*
  // `cart_remove(item_name)` — neither response carries the full cart back,
  // so we patch the local cart optimistically (matching whatever the relay
  // just did to BerryBot's state). A subsequent chat turn or `/state` read
  // will overwrite our patch with the canonical shape; until then the panel
  // reflects the user's last action.
  // `opts.price` is an optimistic-price hint — passed by the menu-click
  // path because MenuPanel already knows the item's formatted price.
  // Used only when synthesising a *new* cart line; never overwrites a
  // price the server has already set on an existing line.
  async addToCart(
    req: CartAddRequest,
    opts: { price?: string } = {},
  ): Promise<void> {
    const api = this.getApi()
    if (!api || !this.conversationId || this.pending) return
    const currentQty = currentLineQty(this.cart, req.item_name)
    if (currentQty + (req.quantity ?? 1) > LINE_QTY_MAX) {
      // Soft cap — short-circuit before the relay call so we don't diverge
      // from the server. Surface the bot-voice notice in the transcript so
      // the user is told why the `+` stopped responding.
      this.messages = [
        ...this.messages,
        { who: 'bot', text: lineQtyCapText() },
      ]
      this.host.requestUpdate()
      return
    }
    this.pending = true
    this.error = null
    this.host.requestUpdate()
    try {
      await api.cartAdd(this.conversationId, req)
      this.cart = mergeAdd(this.cart, req, opts.price)
      // Authoritative cart refresh — CartAddResponse doesn't carry the
      // cart, and `mergeAdd` only seeds {name, quantity} optimistically
      // (no modifier children, no priced extras). Fire-and-forget so the
      // echo message + pending=false don't wait on this round-trip; on
      // success the cart panel re-paints with the server's modifier list
      // and corrected totals (Trello #139).
      void api.getState(this.conversationId).then((s) => {
        if (s?.cart) {
          this.cart = s.cart
          this.host.requestUpdate()
        }
      }).catch(() => {})
      // Echo the addition into the transcript so menu clicks and `+`
      // buttons leave a visible record, mirroring the `AIMessage` BerryBot
      // already injects into LangGraph state for the LLM's benefit
      // (berrybot `e7eebc7`). Same text on both sides so the user's view
      // and the agent's memory agree.
      this.messages = [
        ...this.messages,
        { who: 'bot', text: cartAddEchoText(req) },
      ]
    } catch (e) {
      const picker = modifierHintFrom(e)
      if (picker) {
        // The relay 400'd with a modifier choice — not an error, an inline
        // question. Inject a synthetic bot turn so the chat panel renders
        // the picker below it (same path as a modifier hint from the LLM).
        this.hint = picker
        this.messages = [
          ...this.messages,
          {
            who: 'bot',
            text: describeModifierHint(picker),
            hint: picker,
          },
        ]
      } else {
        this.error = apiErrorMessage(e)
      }
    } finally {
      this.pending = false
      this.host.requestUpdate()
    }
  }

  // Open the modifier picker in chat for a menu item the customer just
  // clicked, without round-tripping `/cart/add` first. Mirrors the catch
  // block in `addToCart` that handles a 400 `choice_needed:modifiers`
  // response — we synthesise the same hint locally so configurable items
  // (any modifier group: required choice, optional choice, or remove
  // adjustments) open the picker instantly. Confirming inside the picker
  // builds an utterance ("Add a Tuna Poke Bowl with Sushi Rice.") that
  // the agent handles via its own add_to_cart tool.
  openModifierPicker(args: {
    itemId: string
    itemName: string
    priceCents?: number
    price?: string
    groups: Array<{
      name: string
      groupType?: string
      minSelections: number
      maxSelections: number
      options: Array<{ name: string; modifierType?: string }>
    }>
  }): void {
    if (!this.conversationId) return
    const hint: import('./types').ModifierChoiceHint = {
      type: 'choice_needed',
      subject: 'modifiers',
      version: 1,
      id: `client-${Date.now().toString(36)}`,
      context: {
        item_id: args.itemId,
        item_name: args.itemName,
        price_cents: args.priceCents ?? 0,
        quantity: 1,
        ...(args.price ? { price: args.price } : {}),
      },
      groups: args.groups.map((g) => ({
        name: g.name,
        group_type: g.groupType,
        min_selections: g.minSelections,
        max_selections: g.maxSelections || g.options.length || 1,
        options: g.options.map((o) => ({
          name: o.name,
          price_cents: 0,
          ...(o.modifierType ? { modifier_type: o.modifierType } : {}),
        })),
      })),
    }
    this.hint = hint
    this.messages = [
      ...this.messages,
      { who: 'bot', text: describeModifierHint(hint), hint },
    ]
    this.host.requestUpdate()
  }

  async removeFromCart(req: CartRemoveRequest): Promise<void> {
    const api = this.getApi()
    if (!api || !this.conversationId || this.pending) return
    this.pending = true
    this.error = null
    this.host.requestUpdate()
    try {
      await api.cartRemove(this.conversationId, req)
      this.cart = mergeRemove(this.cart, req)
      this.messages = [
        ...this.messages,
        { who: 'bot', text: cartRemoveEchoText(req) },
      ]
    } catch (e) {
      this.error = apiErrorMessage(e)
    } finally {
      this.pending = false
      this.host.requestUpdate()
    }
  }

  // Empty the whole cart. The relay has no clear-cart primitive, so this
  // loops `cart_remove` over the current line names — the same direct
  // endpoint the `×` button uses, no LLM turn. Removes run sequentially so
  // each one sees the relay's updated cart (a parallel burst could race the
  // regex-substring lookup in `cart_remove`). On a mid-loop failure the cart
  // reflects whatever was actually removed; the next chat turn / state read
  // resyncs. One bot echo at the end, not one per line.
  async clearCart(): Promise<void> {
    const api = this.getApi()
    const names = (
      (this.cart?.items ?? []) as unknown as Array<Record<string, unknown>>
    )
      .map(lineName)
      .filter(Boolean)
    if (!api || !this.conversationId || this.pending || names.length === 0) return
    this.pending = true
    this.error = null
    this.host.requestUpdate()
    try {
      for (const item_name of names) {
        await api.cartRemove(this.conversationId, { item_name })
        this.cart = mergeRemove(this.cart, { item_name })
        this.host.requestUpdate()
      }
      this.messages = [
        ...this.messages,
        { who: 'bot', text: 'Got it — cleared your cart.' },
      ]
    } catch (e) {
      this.error = apiErrorMessage(e)
    } finally {
      this.pending = false
      this.host.requestUpdate()
    }
  }

  // Submit the accumulated order. The chat-side "Place my order" affordance
  // goes through `send()` (an LLM turn — the agent runs its own submit
  // tool and the response carries `conversation_state: complete` + a
  // thank-you message). This method is the *cart-side* direct path: it
  // hits `/orders/submit` without an LLM turn, and forces the
  // `conversationState = 'complete'` flip locally so the thank-you screen
  // fires for either submit path.
  async submitOrder(): Promise<void> {
    const api = this.getApi()
    if (!api || !this.conversationId || this.submitting) return
    this.submitting = true
    this.error = null
    this.host.requestUpdate()
    // Wait for any in-flight /contact POST so the backend's re-review
    // on submit reads the freshly-patched metadata. See setContact()
    // for the race rationale.
    if (this.contactPending) {
      try { await this.contactPending } catch { /* setContact swallows */ }
    }
    try {
      const resp = (await api.submitOrder(this.conversationId)) as
        | {
            order_id?: string | null
            ref_code?: string | null
            scheduled_for?: string | null
          }
        | undefined
      this.conversationState = 'complete'
      this.orderId = resp?.ref_code || resp?.order_id || null
      this.scheduledFor = resp?.scheduled_for ?? null
    } catch (e) {
      this.error = apiErrorMessage(e)
    } finally {
      this.submitting = false
      this.host.requestUpdate()
    }
  }

  // Fetch a real in-zone delivery address for the current storefront — demo
  // convenience behind the "use a sample address" button. Returns both the
  // display string and the structured `parsed_address` (which carries
  // coordinates, so `setFulfillment` can skip re-geocoding — the reverse-geocoded
  // string is sometimes a Plus Code the geocoder then rejects). Null on error
  // (message surfaced on `error`).
  async requestSampleAddress(): Promise<
    { address: string; parsedAddress: ParsedAddress } | null
  > {
    const api = this.getApi()
    if (!api || !this.conversationId) return null
    try {
      const r = await api.randomAddress(this.conversationId, 'in')
      return { address: r.address, parsedAddress: r.parsed_address }
    } catch (e) {
      this.error = apiErrorMessage(e)
      this.host.requestUpdate()
      return null
    }
  }

  private applyResponse(r: LLMResponse): void {
    if (r.message) {
      this.botTurnSeq++
      this.messages = [
        ...this.messages,
        { who: 'bot', text: r.message, hint: r.hint ?? null },
      ]
    }
    if (r.cart !== undefined) this.cart = r.cart
    if (r.fulfillment_type !== undefined) this.fulfillmentType = r.fulfillment_type
    if (r.hint !== undefined) this.hint = r.hint
    if (r.conversation_state !== undefined) this.conversationState = r.conversation_state
    // `order_id` is only present after submission — keep the previously
    // captured value if a later turn omits it (don't overwrite with null).
    if (r.order_id) this.orderId = r.order_id
    this.opts.onResponse?.(r)
    this.host.requestUpdate()
  }
}

// The relay's `/cart/add` returns 400 with a body that carries a
// `choice_needed:modifiers` hint when an item has unmet required
// modifiers — we render that as the in-chat picker, not as an error.
//
// The body shape took three wrong guesses to pin down (captured live via
// curl, see `test/integration/menu-click-required-modifier.test.ts`):
// BerryBot installs a global FastAPI exception handler
// (`app/main.py:67`) that wraps every `HTTPException(detail=<dict>)` as
// `{error: <detail>, status_code, timestamp}`. So the canonical shape is
// `body.error.hint`. Older / future relays may strip the wrapper
// (`body.hint`) or use FastAPI's default `{detail: <dict>}`; we walk all
// three so a relay change doesn't silently break the picker.
export function modifierHintFrom(e: unknown): import('./types').ModifierChoiceHint | null {
  if (!(e instanceof ApiError) || e.status !== 400) return null
  return findHintIn(e.body, 0)
}

function findHintIn(
  body: unknown,
  depth: number,
): import('./types').ModifierChoiceHint | null {
  if (depth > 3 || !body || typeof body !== 'object') return null
  const obj = body as Record<string, unknown>
  const direct = obj.hint
  if (
    direct &&
    typeof direct === 'object' &&
    (direct as Record<string, unknown>).subject === 'modifiers'
  ) {
    return direct as import('./types').ModifierChoiceHint
  }
  // Walk down through the wrapper layers BerryBot / FastAPI insert
  // (`error` → the inner detail dict; `detail` → FastAPI's default).
  for (const key of ['error', 'detail'] as const) {
    if (obj[key] && typeof obj[key] === 'object') {
      const found = findHintIn(obj[key], depth + 1)
      if (found) return found
    }
  }
  return null
}

// Human-readable prompt for the modifier picker — listed groups + their
// option names, so a typed-only transcript (render-hints=false, no picker UI)
// tells the user what they actually have to choose from rather than just
// "Choose your options for X:". With render-hints on, the picker renders below
// this text; the picker is interactive, the text is just context.
export function describeModifierHint(
  hint: import('./types').ModifierChoiceHint,
): string {
  const item = hint.context?.item_name ?? 'this item'
  const groups = hint.groups ?? []
  if (!groups.length) return `Choose your options for ${item}.`
  const lines = groups.map((group) => {
    const options = (group.options ?? []).map((o) => o.name).filter(Boolean)
    return options.length
      ? `• ${group.name}: ${options.join(', ')}`
      : `• ${group.name}`
  })
  return [`Choose your options for ${item}:`, ...lines].join('\n')
}

// Bot-side echo strings for the direct cart endpoints. Mirrors the
// `AIMessage` text BerryBot writes into LangGraph state on the same
// actions (berrybot `e7eebc7` — `app/api/direct_actions.py`). Keeping
// them aligned means the agent's memory and the user's transcript stay
// in lockstep without an extra LLM round-trip per click.
// Per-line quantity cap for the `+` button path. Soft UI cap — the backend
// has no equivalent, so a chat-driven `cart_add` could still push higher;
// the optimistic `mergeAdd` then clamps the local display to the same max.
export const LINE_QTY_MAX = 99

export function lineQtyCapText(): string {
  return `${LINE_QTY_MAX} is the most we can add of a single item. If you really need more, just say so in chat.`
}

// Customer-facing trailing clause naming the modifiers just picked:
// "" / " with Sushi Rice" / " with Sushi Rice, extra Avocado, no Onions".
// Mirrors `_human_modifier_clause` on the backend so the local echo
// matches what /cart/add stores in LangGraph for the next LLM turn
// (Trello #142). Keep these two in sync.
function humanModifierClause(req: CartAddRequest): string {
  const parts = [
    ...(req.options ?? []),
    ...(req.extras ?? []).map((x) => `extra ${x}`),
    ...(req.removals ?? []).map((x) => `no ${x}`),
  ]
  return parts.length ? ` with ${parts.join(', ')}` : ''
}

function cartAddEchoText(req: CartAddRequest): string {
  const qty = req.quantity ?? 1
  const clause = humanModifierClause(req)
  return qty > 1
    ? `Got it — added ${qty} ${req.item_name}${clause} to your order.`
    : `Got it — added ${req.item_name}${clause} to your order.`
}

function cartRemoveEchoText(req: CartRemoveRequest): string {
  const qty = req.quantity
  return qty && qty > 0
    ? `Got it — removed ${qty} ${req.item_name} from your order.`
    : `Got it — removed ${req.item_name} from your order.`
}

// Cart items come back in two shapes: the ordering agent's `add_to_cart`
// tool keys them by `name`, the direct `/cart/add` endpoint keys them by
// `item`. The display reads `name ?? item`; the merge helpers below match
// the same way so clicking `+` on a line the LLM produced doesn't append a
// duplicate (the bug that surfaced in live testing).
function lineName(it: Record<string, unknown>): string {
  return String((it.name ?? it.item) || '')
}

// True if a cart row stores any configured modifier — used as the
// "different signature" stand-in for the optimistic merge: anything with
// children gets treated as configured regardless of which kind.
function hasModifierChildren(it: Record<string, unknown>): boolean {
  return Array.isArray(it.items) && (it.items as unknown[]).length > 0
}

function currentLineQty(cart: Cart | null, itemName: string): number {
  const needle = itemName.toLowerCase()
  const items = (cart?.items ?? []) as unknown as Array<Record<string, unknown>>
  const hit = items.find((it) => lineName(it).toLowerCase() === needle)
  return hit ? Number(hit.quantity) || 0 : 0
}

// Patch the local cart after a successful `cart_add`: if a line with the same
// (case-insensitive) name AND no configured modifiers exists, bump its
// `quantity`; otherwise append a new line. Mirrors BerryBot's signature-based
// merge invariant (Trello #138) — a configured add never collapses into a
// pre-existing row optimistically. The authoritative server cart lands ~500ms
// later via the post-cartAdd state refresh and corrects any drift.
export function mergeAdd(cart: Cart | null, req: CartAddRequest, price?: string): Cart {
  const qty = req.quantity ?? 1
  const items = (cart?.items ?? []) as unknown as Array<Record<string, unknown>>
  const needle = req.item_name.toLowerCase()
  const reqHasMods =
    !!(req.options?.length || req.extras?.length || req.removals?.length)
  // Only same-name + bare-on-both-sides merges. Anything carrying selected
  // options/extras/removals — on either side — appends a new line.
  const idx = reqHasMods
    ? -1
    : items.findIndex(
        (it) =>
          lineName(it).toLowerCase() === needle && !hasModifierChildren(it),
      )
  // New line: include the optimistic price hint when we have one, so the
  // cart panel renders the line-meta + Subtotal immediately (otherwise
  // the user sees an unpriced row between a menu click and the next
  // chat turn). Existing line: pure quantity bump — never overwrite the
  // server-set price with a client guess.
  const next =
    idx >= 0
      ? items.map((it, i) =>
          i === idx
            ? { ...it, quantity: Math.min(LINE_QTY_MAX, (Number(it.quantity) || 1) + qty) }
            : it,
        )
      : [
          ...items,
          {
            name: req.item_name,
            quantity: qty,
            modifiers: [],
            ...(price ? { price } : {}),
          },
        ]
  return { ...(cart ?? {}), items: next as unknown as CartItem[] }
}

// Patch the local cart after `cart_remove`: find the first line whose name
// contains the requested substring (case-insensitive), matching the relay's
// regex-substring lookup in `cart_remove`. When `quantity` is omitted, drop
// the whole line; when set, decrement by that amount and only drop when the
// result is <= 0. Mirrors BerryBot's direct /cart/remove decrement
// semantics so the optimistic cart matches the eventual server response.
export function mergeRemove(cart: Cart | null, req: CartRemoveRequest): Cart {
  const items = (cart?.items ?? []) as unknown as Array<Record<string, unknown>>
  const needle = req.item_name.toLowerCase()
  const hit = items.findIndex((it) => lineName(it).toLowerCase().includes(needle))
  if (hit < 0) return { ...(cart ?? {}), items: items as unknown as CartItem[] }
  const target = items[hit]
  const currentQty = Number(target.quantity) || 1
  const decrement = req.quantity
  const next =
    decrement && decrement < currentQty
      ? items.map((it, i) =>
          i === hit ? { ...it, quantity: currentQty - decrement } : it,
        )
      : [...items.slice(0, hit), ...items.slice(hit + 1)]
  return { ...(cart ?? {}), items: next as unknown as CartItem[] }
}

// Bucket a send() error into a small telemetry-friendly kind. `http_<n>`
// is a real server response; `network_error` is a fetch that never got
// one (offline, DNS, TLS, mid-flight abort by the browser on hidden
// tabs); `aborted` is an explicit AbortController; anything else falls
// through to the error name so we don't lose signal on new shapes.
function classifySendError(e: unknown): string {
  if (e instanceof ApiError) return `http_${e.status}`
  if (e instanceof Error) {
    if (e.name === 'AbortError') return 'aborted'
    if (e.name === 'TypeError') return 'network_error'
    return e.name || 'unknown'
  }
  return 'unknown'
}

// Rich payload for the `send.failed` client event — every field earns
// its place. The row lands in the conversation log, so this is what the
// team sees when a customer reports "I got Send failed":
//
// - `text`               — the utterance the customer tried to send.
// - `kind`               — bucketed error class (see classifySendError).
// - `message`            — raw error message (e.g. "Failed to fetch").
// - `error_name`         — JS Error.name (`TypeError`, `AbortError`, …)
//                          so a novel kind falling into `unknown` is
//                          still distinguishable in the log.
// - `status`             — HTTP status if we got a response; null if the
//                          fetch never made it that far. Together with
//                          `kind` this is enough to tell "backend 500"
//                          from "browser dropped the request".
// - `body`               — truncated response body when present, so a
//                          5xx-with-error-envelope is inspectable
//                          without pulling server logs.
// - `path`               — path that was hit; matters when the same
//                          send() also does e.g. a 401-refresh call.
// - `duration_ms`        — time from send() start to catch; a very
//                          short duration hints at instant refusal
//                          (offline), a very long one at a hang.
// - `visibility`         — document.visibilityState at fail time. This
//                          is THE field that separates "user
//                          backgrounded the tab and mobile Chrome
//                          killed the fetch" from a real server error.
// - `online`             — navigator.onLine at fail time.
// - `effective_type`     — NetworkInformation effective type (`4g`,
//                          `slow-2g`, …) when the browser exposes it.
// - `ui_events`          — the buffered ui-events that would have
//                          shipped with the utterance's client_context.
//                          Full array (not just a count) so log
//                          reconstruction doesn't have to re-derive them
//                          from earlier rows.
// - `user_agent`, `page_url`, `viewport`, `device_memory`,
//   `hardware_concurrency` — client environment for repro. UA
//                             distinguishes mobile-Chrome-backgrounded
//                             tab aborts from desktop 5xxs; page_url
//                             says which host (oishi/, fatpizza/, an
//                             embed).
// - `correlation`        — {x-request-id, x-vercel-id, cf-ray, …} lifted
//                          from the failing response. Empty when no
//                          response arrived. Join key into upstream
//                          logs.
// - `last_bot_message`   — truncated last-bot text so the log row reads
//                          "user failed while bot said …" without
//                          correlating adjacent rows.
// - `console_errors`     — ring buffer of the last few console.error
//                          entries on the page. Catches JS errors from
//                          unrelated widget code that may have led to
//                          the send failing.
// - `hint_kind`          — last hint kind stashed on the transcript, so
//                          "user failed to send during modifier
//                          picker" reads immediately.
// - `transcript_length`  — how many messages had rendered by the failure,
//                          i.e. how deep into the conversation we were.
// - `cart_item_count`    — items in cart at fail time (real-money impact
//                          proxy: fails at cart=empty are boring; fails
//                          at cart=5 items right before submit are not).
// - `fulfillment_type`   — pickup/delivery or null; correlates with the
//                          step of the flow.
// - `stack`              — truncated JS stack. Last field so the
//                          log-viewer's inline JSON preview shows the
//                          diagnostic fields first.
interface SendFailureExtras {
  durationMs: number
  uiEventsSent: unknown[]
  hintKind: string | null
  transcriptLength: number
  cartItemCount: number
  fulfillmentType: string | null
  lastBotMessage: string | null
}

// Truncated last bot message so the log-viewer row reads "user failed
// to send while bot asked: 'What's the delivery address?'" without the
// team having to correlate rows. Truncation cap balances signal (enough
// of the sentence to recognise) against payload size.
const LAST_BOT_MSG_MAX = 240

function findLastBotMessage(messages: ChatMessage[]): string | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].who === 'bot' && messages[i].text) {
      return truncate(messages[i].text, LAST_BOT_MSG_MAX)
    }
  }
  return null
}

function buildSendFailurePayload(
  text: string,
  e: unknown,
  extras: SendFailureExtras,
): Record<string, unknown> {
  const isApi = e instanceof ApiError
  const err = e instanceof Error ? e : null
  const nav = (globalThis as {
    navigator?: {
      onLine?: boolean
      connection?: { effectiveType?: string }
      userAgent?: string
      hardwareConcurrency?: number
      deviceMemory?: number
    }
  }).navigator
  const doc = (globalThis as {
    document?: { visibilityState?: string }
  }).document
  return {
    text,
    kind: classifySendError(e),
    message: err ? err.message : String(e),
    error_name: err?.name ?? null,
    status: isApi ? (e as ApiError).status : null,
    body: isApi ? truncate(stringifyBody((e as ApiError).body), 500) : null,
    path: isApi ? (e as ApiError).path : null,
    duration_ms: extras.durationMs,
    visibility: doc?.visibilityState ?? null,
    online: nav?.onLine ?? null,
    effective_type: navConnectionType(nav),
    // (2) UA + page URL + screen — repro context. UA distinguishes
    // mobile-Chrome-backgrounded-tab aborts from desktop network 5xxs.
    user_agent: nav?.userAgent ?? null,
    page_url: locationHref(),
    // (8) Screen / device — rarely the cause, but cheap to capture and
    // occasionally distinguishes low-memory Android crashes from
    // desktop stalls.
    viewport: viewportSize(),
    device_memory: (nav as { deviceMemory?: number } | undefined)?.deviceMemory ?? null,
    hardware_concurrency: nav?.hardwareConcurrency ?? null,
    // (1) Correlation ids from response headers — the join keys into
    // Vercel / Cloudflare / berrybot request logs. Empty when the
    // failure was a network abort (no response arrived).
    correlation: isApi ? correlationHeaders((e as ApiError).headers) : {},
    // Conversation-context fields.
    hint_kind: extras.hintKind,
    transcript_length: extras.transcriptLength,
    cart_item_count: extras.cartItemCount,
    fulfillment_type: extras.fulfillmentType,
    // (4) Last bot message — reads "user failed while bot said …".
    last_bot_message: extras.lastBotMessage,
    // (9) The ui_events that would have shipped with this utterance.
    // If the retry (or lack of one) matters for reconstruction, the
    // events themselves are here — no need to re-derive from other
    // rows. Full JSON, capped by the endpoint's payload limits.
    ui_events: extras.uiEventsSent,
    // (6) Ring buffer of recent console.error entries — catches JS
    // errors from unrelated widget code that may have led to this send
    // failing.
    console_errors: consoleBuffer.slice(),
    stack: err?.stack ? truncate(err.stack, 1200) : null,
  }
}

interface NavigatorConnection { effectiveType?: string }
function navConnectionType(
  nav: { connection?: NavigatorConnection } | undefined,
): string | null {
  return nav?.connection?.effectiveType ?? null
}

function locationHref(): string | null {
  const loc = (globalThis as { location?: { href?: string } }).location
  return loc?.href ?? null
}

function viewportSize(): { w: number; h: number } | null {
  const w = (globalThis as { innerWidth?: number; innerHeight?: number })
  return typeof w.innerWidth === 'number' && typeof w.innerHeight === 'number'
    ? { w: w.innerWidth, h: w.innerHeight }
    : null
}

function stringifyBody(body: unknown): string {
  if (body == null) return ''
  if (typeof body === 'string') return body
  try { return JSON.stringify(body) } catch { return String(body) }
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max)}…` : s
}

function nowMs(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now()
}

// ── Console-error ring buffer ─────────────────────────────────────────
// The `send.failed` telemetry needs the last few console.error entries
// so a JS error in unrelated widget code that ultimately caused the
// send to fail is visible in the log row. Install once per page (any
// widget instance triggers it; subsequent installs no-op) and cap at
// CONSOLE_BUFFER_MAX entries — ring semantics keep the *most recent*
// errors, which is what a diagnostic snapshot wants.
const CONSOLE_BUFFER_MAX = 5
const CONSOLE_MSG_MAX = 500

interface ConsoleEntry { at: string; message: string }
const consoleBuffer: ConsoleEntry[] = []
let consoleHookInstalled = false

function installConsoleHook(): void {
  if (consoleHookInstalled) return
  const c = (globalThis as { console?: Console }).console
  if (!c || typeof c.error !== 'function') return
  const original = c.error.bind(c)
  c.error = ((...args: unknown[]) => {
    try {
      const message = args
        .map((a) => (a instanceof Error ? (a.stack || a.message) : stringifyBody(a)))
        .join(' ')
      consoleBuffer.push({
        at: new Date().toISOString(),
        message: truncate(message, CONSOLE_MSG_MAX),
      })
      while (consoleBuffer.length > CONSOLE_BUFFER_MAX) consoleBuffer.shift()
    } catch {
      // Never break console.error — telemetry is a courtesy.
    }
    original(...args)
  }) as typeof c.error
  consoleHookInstalled = true
}

// Install on module load so we capture errors that happen before any
// ConversationController is constructed (initial widget mount, auth
// bootstrap, etc.). Guarded by the flag above.
installConsoleHook()
